import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
// import { supabase } from '@/lib/supabase';
import { ListMusic, QrCode, PoundSterling, DollarSign, Euro, Settings, BarChart, Users, Heart, MapPin } from 'lucide-react';
import Header from '@/components/landing/Header';
import ArtistFlowAnimations from '@/components/landing/ArtistFlowAnimations';
import LandingStatsTicker from '@/components/landing/LandingStatsTicker';
import { detectCountry, getCurrencyForCountry } from '@/lib/currency';

function getPaidIcon(currency) {
  const c = (currency || 'GBP').toUpperCase();
  if (c === 'GBP') return PoundSterling;
  if (c === 'EUR') return Euro;
  return DollarSign;
}
const FeatureBenefitCard = ({
  icon: Icon,
  title,
  description,
  delay
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} transition={{
  duration: 0.5,
  delay
}} viewport={{
  once: true
}} className="flex items-start space-x-4">
    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
      <Icon className="w-6 h-6 text-blue-400" />
    </div>
    <div>
      <h3 className="text-xl font-bold text-white">{title}</h3>
      <p className="mt-1 text-gray-400">{description}</p>
    </div>
  </motion.div>;
const HomePage = () => {
  const [landingCurrency, setLandingCurrency] = useState('GBP');

  useEffect(() => {
    try {
      setLandingCurrency(getCurrencyForCountry(detectCountry()));
    } catch {
      setLandingCurrency('GBP');
    }
  }, []);

  const PaidIcon = getPaidIcon(landingCurrency);

  return <>
      <Helmet>
        <title>Your Song Request - Your Stage. Their Soundtrack.</title>
        <meta name="description" content="Bridge the gap between you and your audience. Your Song Request lets your audience pay to choose their favourite songs from your setlist. You control the songs; they pay to hear them." />
        <meta property="og:title" content="Your Song Request - Your Stage. Their Soundtrack." />
        <meta property="og:description" content="Bridge the gap between you and your audience. Your Song Request lets your audience pay to choose their favourite songs from your setlist. You control the songs; they pay to hear them." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1516280440614-376394488844?q=80&w=2070&auto=format&fit=crop" />
        <link rel="canonical" href="https://yoursongrequest.com" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-200 font-['Inter']">
        <Header />
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center text-center px-4">
          <div className="absolute inset-0 overflow-hidden">
            <img alt="A musician performing with a guitar in a dimly lit venue" className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1494403912729-e619111312dd" />
            <div className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm"></div>
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="relative z-10 mt-40">
            <h1 className="text-5xl md:text-7xl font-bold text-white multi-glow-1">
              <span className="block">Stop Taking Requests.</span>
              <span className="block">Start Selling Them!</span>
            </h1>
            <p className="max-w-3xl mx-auto mt-4 text-lg md:text-xl text-gray-300">
              Bridge the gap between you and your audience. Your Song Request lets your audience pay to choose their favourite songs from your setlist.
              <br />
              <strong>
                <span className="block md:inline">You control the songs.</span>{' '}
                <span className="block md:inline">They pay to hear them.</span>
              </strong>
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-gray-700 hover:bg-gray-600 text-white font-bold text-lg py-4 px-8 rounded-lg transform hover:scale-105 transition-transform duration-300"
                onClick={() => {
                  const howItWorksSection = document.getElementById('how-it-works');
                  if (howItWorksSection) {
                    howItWorksSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Learn More
              </Button>
              <Link to="/signup">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg py-4 px-8 rounded-lg neon-glow transform hover:scale-105 transition-transform duration-300">
                  Sign Up For Free
                </Button>
              </Link>
            </div>
            
            {/* App Screenshots */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8 flex flex-row items-center justify-center gap-4 max-w-4xl mx-auto"
            >
              <div className="flex-shrink-0">
                <img 
                  alt="Your Song Request app interface showing live session with song requests" 
                  className="w-48 h-auto" 
                  src="/app-screenshot-1.png"
                />
              </div>
              <div className="flex-shrink-0">
                <img 
                  alt="Push notifications from Your Song Request showing new song requests" 
                  className="w-48 h-auto" 
                  src="/app-screenshot-2.png"
                />
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Social Proof Bar */}
        <section className="py-8 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-sm font-semibold text-gray-400 tracking-widest uppercase">
              POWERING ARTISTS IN PUBS, BARS, AND VENUES ACROSS THE UK
            </p>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-20 sm:py-28 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white multi-glow-2">It's as Easy <br className="sm:hidden" /> as 1, 2, FREE!</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
              <motion.div initial={{
              opacity: 0,
              y: 50
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} viewport={{
              once: true
            }}>
                <div className="mx-auto w-16 h-16 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-6">
                  <ListMusic className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-2xl font-bold text-white">Build Your Setlist</h3>
                <p className="mt-2 text-gray-400">
                  Sign up for free and add your entire repertoire of songs. Set your own price for each request.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 50
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} viewport={{
              once: true
            }}>
                <div className="mx-auto w-16 h-16 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-6">
                  <QrCode className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-2xl font-bold text-white">Go Live</h3>
                <p className="mt-2 text-gray-400">When your gig starts, share your unique QR/URL code with the audience. No app download needed..</p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 50
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} viewport={{
              once: true
            }}>
                <div className="mx-auto w-16 h-16 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-6">
                  <PaidIcon className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-2xl font-bold text-white">Get Paid</h3>
                <p className="mt-2 text-gray-400">Watch paid requests roll in on your live dashboard. Control the show and cash out your earnings.</p>
              </motion.div>
            </div>
            <LandingStatsTicker currency={landingCurrency} />
          </div>
        </section>

        {/* Features & Benefits Section */}
        <section className="py-20 sm:py-28 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white multi-glow-3">Your Stage, Your Rules.</h2>
            </div>
            
            {/* Animation Component - Directly under heading */}
            <div className="mb-16">
              <ArtistFlowAnimations currency={landingCurrency} />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 text-left">
              <FeatureBenefitCard icon={Settings} title="Total Setlist Control" description="You decide which songs are available and set your own prices. Mark songs as 'played' to remove them from the list for the rest of the night. Giving you full control of pacing and genre/style of your set as you normally do." delay={0.1} />
              <FeatureBenefitCard icon={BarChart} title="A Real-Time Dashboard" description="Manage incoming requests, see your earnings stack up, and control the flow of your gig—all from your phone or tablet. You can even control it whilst playing the guitar, you big show off! It's that easy!" delay={0.2} />
              <FeatureBenefitCard icon={Users} title="Engage Your Audience" description="Give your fans a direct way to participate in the show. A paid request is the ultimate compliment and a great way to build a memorable connection." delay={0.3} />
              <FeatureBenefitCard icon={Heart} title="More Than Just a Request" description="Unlike a passive tip jar, YSR is an interactive experience that adds value to both you and your audience, leading to significantly higher earnings." delay={0.4} />
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 sm:py-28 bg-black">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white multi-glow-4">Simple, Transparent Pricing.</h2>
            <p className="mt-4 text-lg text-gray-400">No monthly fees. No setup costs. No surprises.</p>
            <p className="mt-6 text-gray-300">
              It is completely free to sign up and list your songs. We only make money when you do.
            </p>
            <ul className="mt-6 text-left text-gray-300 space-y-3 list-disc list-inside">
              <li>
                Song Requests: YSR keeps a 30% platform fee from each request transaction. This covers all card
                processing, hosting, and continued development of Your Song Request.
              </li>
              <li>
                Tips: You earned them, you keep them. We deduct a 5% processing fee to cover the transaction cost.
              </li>
            </ul>
            <p className="mt-6 text-gray-300">
              You have total control - price your songs however you like and keep the lion&apos;s share of what you earn.
            </p>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 sm:py-28 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white multi-glow-5">Ready to Transform Your Live Gigs?</h2>
            <p className="mt-4 text-lg text-gray-400 max-w-2xl mx-auto">
              Stop leaving money on the table. Give your audience what they want and get paid for it.
            </p>
            <Link to="/signup">
              <Button size="lg" className="mt-10 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg py-4 px-8 rounded-lg neon-glow transform hover:scale-105 transition-transform duration-300">
                SIGN UP NOW FOR FREE
              </Button>
            </Link>
          </div>
        </section>

        {/* Find A Performer Map Section */}
        <section className="relative bg-black py-12 sm:py-16 md:py-20">
          <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Heading */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center mb-8 md:mb-12"
            >
              <h2 className="text-3xl md:text-5xl font-bold text-white multi-glow-6">Find a YSR Performer!</h2>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative rounded-xl overflow-hidden"
            >
              {/* Map Image */}
              <div className="relative w-full">
                <img 
                  src="/uk-ireland-map.png" 
                  alt="Map of UK and Ireland showing YSR performer locations"
                  className="w-full h-auto object-cover"
                  style={{ 
                    minHeight: '300px',
                    maxHeight: '450px',
                    objectFit: 'cover',
                    aspectRatio: '16 / 9'
                  }}
                  onError={(e) => {
                    // Fallback if image doesn't exist - create a placeholder
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentElement.querySelector('.map-placeholder');
                    if (placeholder) placeholder.style.display = 'flex';
                  }}
                />
                {/* Placeholder if image doesn't load */}
                <div 
                  className="map-placeholder hidden items-center justify-center bg-gradient-to-br from-blue-900 via-gray-900 to-blue-800"
                  style={{ 
                    minHeight: '300px',
                    maxHeight: '450px',
                    width: '100%',
                    aspectRatio: '16 / 9'
                  }}
                >
                  <div className="text-center text-white">
                    <MapPin className="w-16 h-16 mx-auto mb-4 text-blue-400" />
                    <p className="text-lg font-semibold">UK & Ireland Map</p>
                    <p className="text-sm text-gray-400 mt-2">Add map image to /public/uk-ireland-map.png</p>
                  </div>
                </div>
                
                {/* Overlay Button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Link to="/find-performer">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button 
                        size="lg" 
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg md:text-xl py-4 px-8 md:py-5 md:px-10 rounded-lg shadow-2xl border-2 border-white/20 backdrop-blur-sm transform transition-all duration-300"
                        style={{
                          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(59, 130, 246, 0.4)'
                        }}
                      >
                        Find A Performer
                      </Button>
                    </motion.div>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black border-t border-gray-800 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">
                © 2026 Your Song Request. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500">
                <Link to="/refund-policy" className="hover:text-gray-400 transition-colors">
                  Refund Policy
                </Link>
                <span className="text-gray-600">•</span>
                <Link to="/terms-of-service" className="hover:text-gray-400 transition-colors">
                  Terms & Conditions
                </Link>
                <span className="text-gray-600">•</span>
                <Link to="/privacy-policy" className="hover:text-gray-400 transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-800 text-center">
              <p className="text-xs text-gray-600">
                Registered Business Address: 6 Pendle Close, Thornton-Cleveley's, FY5 3WB
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>;
};
export default HomePage;